import React, { useState, useCallback, useEffect } from 'react';
import { ChevronRight, Info, Circle, Eye, ChevronUp, ChevronDown } from 'lucide-react';
import type { CFEntity } from '../../types/campaignFinance';
import type { UseCFEntityReturn } from '../../hooks/useCFEntity';
import { fmtDate, fmtMoney, getPartyHex } from '../../utils/campaignFinanceUtils';
import CFRecordModal from './CFRecordModal';
import CFTransactionModal from './CFTransactionModal';
import LoadingList from '@shared/ui/LoadingList';
import { SortIcon } from '../icons';
import SegmentedControl from '../SegmentedControl';
import CommitteeLink from '../committee/CommitteeLink';

interface CFTransactionsExplorerProps {
  activeTab: CFEntity;
  onTabChange: (tab: CFEntity) => void;
  entities: Record<CFEntity, UseCFEntityReturn>;
  cycleLabel?: string;
  /** Which entity tabs to show. Defaults to all three (committee dashboard). */
  tabs?: CFEntity[];
  /** When set, contribution Source / Counterparty cells become links that launch a new search for that entity. */
  onEntitySearch?: (kind: 'contributor' | 'committee', name: string) => void;
  /**
   * Set on single-committee surfaces (the committee dashboard): hides the
   * expenditures "Paid By" grouping, which is degenerate there — every row
   * is paid by the same committee.
   */
  committeeScoped?: boolean;
}

interface GroupOpt { key: string; label: string; }

const GROUP_OPTS: Record<CFEntity, GroupOpt[]> = {
  contributions: [{ key: 'none', label: 'None (ledger)' }, { key: 'contributor', label: 'Source' }, { key: 'committee', label: 'Counterparty' }],
  expenditures: [{ key: 'none', label: 'None (ledger)' }, { key: 'committee', label: 'Paid By' }, { key: 'payee', label: 'Payee' }, { key: 'etype', label: 'Expenditure Type' }],
  loans: [{ key: 'none', label: 'None (ledger)' }, { key: 'lender', label: 'Lender type' }],
};

const FLAT_HEADERS: Record<CFEntity, [string, string, string, string]> = {
  contributions: ['Source', 'Counterparty', 'Type', 'Amount'],
  expenditures: ['Payee', 'Purpose', 'Type', 'Amount'],
  loans: ['Lender', 'Event', 'Type', 'Amount'],
};

const TAB_ACCENT: Record<CFEntity, string> = {
  contributions: 'var(--cf-in)',
  expenditures: 'var(--cf-out)',
  loans: 'var(--cf-loan)',
};

const CAVEAT: Record<CFEntity, string> = {
  contributions: 'Amendment rows are included (they net by inclusion); negative rows are refunds — shown, not hidden. Switch Group by to pivot the same set.',
  expenditures: 'The listed payee may be a processor, not the end merchant — caveat any vendor ranking. Returned expenditures show as negatives.',
  loans: 'Outstanding balance (exposure) is the headline; the "total borrowed" rollup is held pending the loan-grain probe. ~99% are candidate self-loans.',
};

// Every entity has a per-record detail modal, so flat rows carry a trailing eye column.
const FLAT_GRID = 'grid-cols-[110px_minmax(0,1.7fr)_minmax(0,1.7fr)_150px_110px_44px]';

// Backend sort fields per entity for the sortable flat-ledger columns
// (Date / primary / secondary / type chip / Amount). The secondary and type
// fields sort by the record's underlying column, which is what the cell renders
// (or leads its fallback chain).
const SORT_FIELDS: Record<CFEntity, { date: string; primary: string; secondary: string; type: string; amount: string }> = {
  contributions: { date: 'contribution_date', primary: 'computed_contributor_name', secondary: 'committee_name', type: 'contribution_type', amount: 'contribution_amount' },
  expenditures: { date: 'expenditure_date', primary: 'last_name', secondary: 'explanation', type: 'disbursement_type', amount: 'expenditure_amount' },
  loans: { date: 'loan_date', primary: 'name', secondary: 'type', type: 'loan_source_type', amount: 'loan_amount' },
};

// Unified grouped layout: Latest date · Group · Records · Total · Share (rows expand to records).
const GROUP_GRID = 'grid-cols-[110px_minmax(0,2fr)_90px_110px_1.1fr]';

// Backend sort fields for the grouped views. Contributions aggregate under
// explicit column names (per group mode); expenditures/loans group under the
// generic name/txns/last_date/total aliases. Share isn't sortable — it's Total
// as a percentage, so sorting Total orders it too.
function groupSortFields(entity: CFEntity, groupBy: string): { latest: string; name: string; records: string; total: string } {
  if (entity === 'contributions') {
    return {
      latest: 'last_contribution_date',
      name: groupBy === 'committee' ? 'committee_name' : 'computed_contributor_name',
      records: 'contribution_count',
      total: 'total_amount',
    };
  }
  return { latest: 'last_date', name: 'name', records: 'txns', total: 'total' };
}

// The exact filter that expands a group into its individual records, per entity/grouping.
function childFilter(entity: CFEntity, groupBy: string, name: string): Record<string, string> {
  if (entity === 'expenditures') {
    if (groupBy === 'committee') return { committee_exact: name };
    if (groupBy === 'etype') return { expenditure_type_exact: name };
    return { payee: name };
  }
  if (entity === 'loans') return { loan_source_type: name };
  return groupBy === 'committee' ? { committee_exact: name } : { contributor_exact: name };
}

interface GroupRow { name: string; sub?: string; count: number; total: number; lastDate?: string }

function normalizeGroup(entity: CFEntity, g: any): GroupRow {
  if (entity === 'contributions') {
    // Grouped by contributor the row carries computed_contributor_name; grouped by
    // counterparty (committee) it carries committee_name + candidate_name instead.
    const byCommittee = g.computed_contributor_name === undefined && g.committee_name !== undefined;
    return {
      name: (byCommittee ? g.committee_name : g.computed_contributor_name) || 'Anonymous',
      sub: (byCommittee ? g.candidate_name : g.contributor_type) || undefined,
      count: Number(g.contribution_count) || 0,
      total: Number(g.total_amount) || 0,
      lastDate: g.last_contribution_date,
    };
  }
  return {
    name: g.name || 'Unknown',
    count: Number(g.txns) || 0,
    total: Number(g.total) || 0,
    lastDate: g.last_date,
  };
}

interface FlatRow {
  date: string;
  primary: string; primarySub?: string;
  secondary: string; secondarySub?: string; dotColor?: string;
  chip: string; chipCls: string;
  amount: number; amountCls: string;
}

const chipBase = 'rounded-full px-[9px] py-[2px] text-[10.5px] font-semibold whitespace-nowrap';

function contribChip(type?: string): { label: string; cls: string } {
  const t = (type || '').toLowerCase();
  if (t.includes('non-monetary') || t.includes('in-kind')) return { label: 'In-kind', cls: 'bg-cf-loan-tint text-cf-loan-deep' };
  if (t.includes('return') || t.includes('refund')) return { label: 'Refund', cls: 'bg-cf-alert-soft text-cf-alert' };
  if (t.includes('monetary')) return { label: 'Monetary', cls: 'bg-cf-in-tint text-cf-in-value' };
  return { label: type || '—', cls: 'bg-primary-100 text-primary-700' };
}

function normalizeRow(entity: CFEntity, r: any): FlatRow {
  if (entity === 'expenditures') {
    const neg = Number(r.expenditure_amount) < 0;
    const returned = (r.disbursement_type || '').toLowerCase().includes('return');
    return {
      date: r.expenditure_date,
      primary: r.payee_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || 'Unknown',
      primarySub: r.city ? `${r.city}${r.state ? `, ${r.state}` : ''}` : undefined,
      secondary: r.explanation || r.expenditure_type || '—',
      chip: r.disbursement_type || r.expenditure_type || '—',
      chipCls: returned ? 'bg-cf-alert-soft text-cf-alert' : 'bg-primary-100 text-primary-700',
      amount: Number(r.expenditure_amount) || 0,
      amountCls: neg ? 'text-cf-alert' : 'text-text-primary dark:text-text-dark-primary',
    };
  }
  if (entity === 'loans') {
    const isPayment = r.type === 'P';
    const amount = isPayment ? Number(r.payment_amount) || 0 : Number(r.loan_amount) || 0;
    const selfLoan = (r.loan_source_type || '').toLowerCase().startsWith('candidate');
    return {
      date: r.loan_date || r.payment_date,
      primary: r.name || 'Unknown',
      primarySub: r.loan_source_type || (r.interest_rate != null ? `Interest ${Number(r.interest_rate)}%` : undefined),
      secondary: isPayment ? 'Repayment' : 'Origination',
      secondarySub: r.loan_balance != null ? `Balance ${fmtMoney(Number(r.loan_balance))}` : undefined,
      chip: selfLoan ? 'Self-loan' : (r.type === 'O' ? 'Origination' : 'Payment'),
      chipCls: 'bg-cf-loan-soft text-cf-loan',
      amount,
      amountCls: 'text-cf-loan',
    };
  }
  // contributions
  const neg = Number(r.contribution_amount) < 0;
  const chip = contribChip(r.contribution_type);
  return {
    date: r.contribution_date,
    primary: r.computed_contributor_name || 'Anonymous',
    primarySub: r.contributor_type || undefined,
    secondary: r.committee_name || '—',
    secondarySub: r.candidate_name || undefined,
    dotColor: r.candidate_party ? getPartyHex(r.candidate_party) : undefined,
    chip: chip.label,
    chipCls: chip.cls,
    amount: Number(r.contribution_amount) || 0,
    amountCls: neg ? 'text-cf-alert' : 'text-text-primary dark:text-text-dark-primary',
  };
}

function totalLabel(entity: CFEntity, e: UseCFEntityReturn): string {
  if (entity === 'loans') {
    const owed = e.selfFunding?.outstandingBalance ?? (Number(e.summary?.outstanding_balance) || 0);
    return `${fmtMoney(owed, { compact: true })} owed`;
  }
  return fmtMoney(Number(e.summary?.total_amount) || 0, { compact: true });
}

const CFTransactionsExplorer: React.FC<CFTransactionsExplorerProps> = ({
  activeTab, onTabChange, entities, cycleLabel, tabs: visibleTabs, onEntitySearch, committeeScoped,
}) => {
  const e = entities[activeTab];
  const groupOpts = (committeeScoped && activeTab === 'expenditures')
    ? GROUP_OPTS.expenditures.filter(o => o.key !== 'committee')
    : GROUP_OPTS[activeTab];
  const grouped = e.grouped;
  const rows = e.rows;
  const [pHead, sHead, tHead, aHead] = FLAT_HEADERS[activeTab];
  const showEye = true;
  const flatGrid = FLAT_GRID;
  const caveat = activeTab === 'contributions' && cycleLabel
    ? `Cycle-scoped to ${cycleLabel}. ${CAVEAT.contributions}`
    : CAVEAT[activeTab];

  // Per-record detail modal (all entities) + inline expansion of a grouped source.
  const [modalRecord, setModalRecord] = useState<{ entity: CFEntity; record: any } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [childrenByName, setChildrenByName] = useState<Record<string, any[]>>({});
  const [loadingNames, setLoadingNames] = useState<Set<string>>(new Set());

  // Reset expansion whenever the tab or grouping changes.
  useEffect(() => {
    setExpanded(new Set());
    setChildrenByName({});
    setLoadingNames(new Set());
  }, [activeTab, e.groupBy]);

  // Expand a group into its individual records, reusing the entity's current scope
  // (so the children match the group aggregate) plus the group's exact-match filter.
  const entityFetchRecords = e.fetchRecords;
  const entityGroupBy = e.groupBy;
  const fetchChildren = useCallback(async (name: string) => {
    setLoadingNames(prev => new Set(prev).add(name));
    try {
      const list = await entityFetchRecords(childFilter(activeTab, entityGroupBy, name));
      setChildrenByName(prev => ({ ...prev, [name]: list }));
    } catch {
      setChildrenByName(prev => ({ ...prev, [name]: [] }));
    } finally {
      setLoadingNames(prev => { const next = new Set(prev); next.delete(name); return next; });
    }
  }, [entityFetchRecords, activeTab, entityGroupBy]);

  const toggleExpand = useCallback((name: string) => {
    if (!name) return;
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
        if (childrenByName[name] === undefined && !loadingNames.has(name)) void fetchChildren(name);
      }
      return next;
    });
  }, [childrenByName, loadingNames, fetchChildren]);

  const allTabs: { key: CFEntity; label: string }[] = [
    { key: 'contributions', label: 'Contributions' },
    { key: 'expenditures', label: 'Expenditures' },
    { key: 'loans', label: 'Loans' },
  ];
  const tabs = visibleTabs ? allTabs.filter(t => visibleTabs.includes(t.key)) : allTabs;

  const groupLabel = groupOpts.find(o => o.key === e.groupBy)?.label || 'Group';

  // Sortable header cell: toggles direction on the active column, otherwise sorts
  // the new column by its natural default (names asc, date/amount/count desc).
  // activeSortBy defaults to the raw hook state; the grouped view passes its own
  // effective value (the hook still holds the flat default right after a load,
  // which the backend maps to total-desc in group mode).
  const sortFields = SORT_FIELDS[activeTab];
  const applySort = (field: string, defaultDir: 'asc' | 'desc', activeSortBy = e.sortBy) => {
    const dir: 'asc' | 'desc' = activeSortBy === field ? (e.sortDir === 'asc' ? 'desc' : 'asc') : defaultDir;
    e.setSort(field, dir);
  };
  const sortHeader = (label: string, field: string, defaultDir: 'asc' | 'desc', alignRight = false, activeSortBy = e.sortBy) => {
    const activeCol = activeSortBy === field;
    return (
      <button
        type="button"
        onClick={() => applySort(field, defaultDir, activeSortBy)}
        className={`flex items-center gap-1 uppercase tracking-[0.04em] hover:text-text-primary dark:hover:text-text-dark-primary transition-colors ${alignRight ? 'justify-end' : ''}`}
      >
        <span>{label}</span>
        {activeCol
          ? (e.sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
          : <SortIcon className="w-3 h-3 opacity-40" />}
      </button>
    );
  };

  const renderFlatRow = (raw: any, key: React.Key, isChild = false) => {
    const r = normalizeRow(activeTab, raw);
    // Contribution Source becomes a search link only when the row has a real underlying
    // name ("Anonymous"/"Unknown" fallbacks stay plain text). Counterparty is a committee:
    // it opens the committee card, whose "Open full dashboard" drills down (PRD §5.12, O3).
    const sourceSearch = onEntitySearch && activeTab === 'contributions' && raw.computed_contributor_name;
    const counterpartyCommittee = activeTab === 'contributions' && raw.committee_name;
    return (
      <div key={key} className={`grid ${flatGrid} gap-3 px-5 py-[11px] border-b border-border-light/60 dark:border-border-dark/50 items-center ${isChild ? 'bg-background-secondary/40 dark:bg-background-dark-tertiary/30' : ''}`}>
        <span className="tabular-nums text-xs text-text-secondary dark:text-text-dark-secondary">{r.date ? fmtDate(r.date) : '—'}</span>
        <div className="min-w-0 flex items-center gap-1.5">
          {isChild && <span className="text-accent-400 flex-none">↳</span>}
          <div className="min-w-0">
            {sourceSearch ? (
              <button
                type="button"
                onClick={() => onEntitySearch('contributor', raw.computed_contributor_name)}
                className="block max-w-full truncate text-left text-[12.5px] text-primary-700 dark:text-primary-dark-400 hover:underline"
                title={`Search contributions from ${r.primary}`}
              >
                {r.primary}
              </button>
            ) : (
              <div className="text-[12.5px] text-text-primary dark:text-text-dark-primary truncate">{r.primary}</div>
            )}
            {r.primarySub && <div className="text-[10.5px] text-accent-400 truncate">{r.primarySub}</div>}
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[12.5px] text-text-primary dark:text-text-dark-primary flex items-center gap-1.5 truncate">
            {r.dotColor && <Circle className="w-[7px] h-[7px] flex-none" fill={r.dotColor} stroke="none" />}
            {counterpartyCommittee ? (
              <CommitteeLink
                coId={raw.co_id || raw.committee_co_id}
                name={r.secondary}
                className="truncate text-primary-700 dark:text-primary-dark-400"
              />
            ) : (
              <span className="truncate">{r.secondary}</span>
            )}
          </div>
          {r.secondarySub && <div className="text-[10.5px] text-accent-400 truncate">{r.secondarySub}</div>}
        </div>
        <span><span className={`${chipBase} ${r.chipCls}`}>{r.chip}</span></span>
        <span className={`tabular-nums text-right font-semibold text-[12.5px] ${r.amountCls}`}>{fmtMoney(r.amount)}</span>
        {showEye && (
          <span className="text-right">
            <button
              type="button"
              onClick={() => setModalRecord({ entity: activeTab, record: raw })}
              className="text-accent-400 hover:text-primary-800 dark:hover:text-primary-dark-400 transition-colors"
              aria-label="View record details"
            >
              <Eye className="w-4 h-4" />
            </button>
          </span>
        )}
      </div>
    );
  };

  // One grouped view for every entity: Latest · Group (expandable) · Records · Total · Share.
  const renderGrouped = () => {
    const gf = groupSortFields(activeTab, e.groupBy);
    // Right after a (re)load the hook still holds the flat-ledger default sort,
    // which the backend maps to total-desc in group mode — mirror that here so
    // the Total column shows as the active sort.
    const groupSortBy = [gf.latest, gf.name, gf.records, gf.total].includes(e.sortBy) ? e.sortBy : gf.total;
    // Share = this group's total as a % of the filtered-set total (from the summary,
    // which spans the whole set, not just the current page). The bar and the % label
    // both use this so they agree.
    const grandTotal = Math.abs(
      activeTab === 'loans'
        ? (e.selfFunding?.outstandingBalance ?? Number(e.summary?.outstanding_balance ?? 0))
        : Number(e.summary?.total_amount ?? 0)
    );
    return (
      <div>
        <div className={`grid ${GROUP_GRID} gap-3.5 px-5 py-2.5 table-header text-[10px] font-semibold tracking-[0.04em] uppercase`}>
          {sortHeader('Latest', gf.latest, 'desc', false, groupSortBy)}
          {sortHeader(groupLabel, gf.name, 'asc', false, groupSortBy)}
          {sortHeader('Records', gf.records, 'desc', true, groupSortBy)}
          {sortHeader('Total', gf.total, 'desc', true, groupSortBy)}
          <span>Share</span>
        </div>
        {rows.map((raw: any, i: number) => {
          const g = normalizeGroup(activeTab, raw);
          const isOpen = expanded.has(g.name);
          const kids = childrenByName[g.name];
          const share = grandTotal > 0 ? (Math.abs(g.total) / grandTotal) * 100 : 0;
          return (
            <React.Fragment key={`${g.name}-${i}`}>
              <div className={`grid ${GROUP_GRID} gap-3.5 px-5 py-[11px] border-b border-border-light/60 dark:border-border-dark/50 items-center`}>
                <span className="tabular-nums text-xs text-text-secondary dark:text-text-dark-secondary">{g.lastDate ? fmtDate(g.lastDate) : '—'}</span>
                <div className="min-w-0 flex items-start gap-1.5">
                  <button
                    type="button"
                    onClick={() => toggleExpand(g.name)}
                    className="mt-0.5 text-accent-400 hover:text-primary-800 dark:hover:text-primary-dark-400 transition-colors flex-none"
                    aria-label={isOpen ? `Collapse ${g.name}` : `Expand ${g.name}`}
                  >
                    <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </button>
                  <div className="min-w-0">
                    {/* Counterparty / Paid By groups are committees named only — the card resolves the name. */}
                    {e.groupBy === 'committee' && (activeTab === 'contributions' || activeTab === 'expenditures') && g.name !== 'Unknown' && g.name !== 'Anonymous' ? (
                      <CommitteeLink
                        coId={raw.co_id || null}
                        name={g.name}
                        className="block max-w-full truncate font-semibold text-[12.5px] text-text-primary dark:text-text-dark-primary hover:text-primary-700 dark:hover:text-primary-dark-400"
                      />
                    ) : (
                      <div className="font-semibold text-[12.5px] text-text-primary dark:text-text-dark-primary truncate">{g.name}</div>
                    )}
                    {g.sub && <div className="text-[10.5px] text-accent-400 truncate">{g.sub}</div>}
                  </div>
                </div>
                <span className="tabular-nums text-right text-xs text-text-secondary dark:text-text-dark-secondary">{g.count.toLocaleString()}</span>
                <span className="tabular-nums text-right font-bold text-[13px] text-text-primary dark:text-text-dark-primary">{fmtMoney(g.total, { compact: true })}</span>
                <div className="flex items-center gap-2 self-center">
                  <div className="h-[7px] flex-1 bg-primary-100 dark:bg-background-dark-secondary rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, share)}%`, background: 'linear-gradient(90deg,var(--cf-out),var(--cf-out-light))' }} />
                  </div>
                  <span className="tabular-nums text-[11px] text-text-secondary dark:text-text-dark-secondary flex-none w-[42px] text-right">
                    {share >= 0.1 ? `${share.toFixed(1)}%` : share > 0 ? '<0.1%' : '—'}
                  </span>
                </div>
              </div>
              {isOpen && (
                loadingNames.has(g.name) || kids === undefined ? (
                  <div className="px-5 py-2 pl-12 text-xs text-accent-400 border-b border-border-light/60 dark:border-border-dark/50">Loading records…</div>
                ) : kids.length === 0 ? (
                  <div className="px-5 py-2 pl-12 text-xs text-accent-400 border-b border-border-light/60 dark:border-border-dark/50">No individual records found.</div>
                ) : (
                  kids.map((c, ci) => renderFlatRow(c, `${g.name}-child-${c.id || ci}`, true))
                )
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderFlat = () => (
    <div>
      <div className={`grid ${flatGrid} gap-3 px-5 py-2.5 table-header text-[10px] font-semibold tracking-[0.04em] uppercase`}>
        {sortHeader('Date', sortFields.date, 'desc')}
        {sortHeader(pHead, sortFields.primary, 'asc')}
        {sortHeader(sHead, sortFields.secondary, 'asc')}
        {sortHeader(tHead, sortFields.type, 'asc')}
        {sortHeader(aHead, sortFields.amount, 'desc', true)}
        {showEye && <span />}
      </div>
      {rows.map((raw: any, i: number) => renderFlatRow(raw, raw.id || i))}
    </div>
  );

  return (
    <div className="card !p-0 !rounded-lg overflow-hidden">
      {/* Tabs — hidden when a single entity is shown (e.g. a contributor's ledger). */}
      {tabs.length > 1 && (
      <div className="px-5 pt-3.5 border-b border-border-light dark:border-border-dark">
        <div className="flex gap-0.5">
          {tabs.map(t => {
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => onTabChange(t.key)}
                className="relative bg-transparent border-none px-[15px] pt-2.5 pb-3 text-[13px] font-semibold cursor-pointer text-text-primary dark:text-text-dark-primary"
              >
                {t.label}
                {isActive && (
                  <span className="absolute left-[11px] right-[11px] -bottom-px h-[2.5px] rounded-[3px]" style={{ background: TAB_ACCENT[t.key] }} />
                )}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Group-by toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-3 bg-background-tertiary dark:bg-background-dark-tertiary border-b border-border-light dark:border-border-dark">
        <div className="flex items-center gap-2.5">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-accent-500 dark:text-text-dark-tertiary">Group by</span>
          <SegmentedControl
            ariaLabel="Group by"
            value={(e.groupBy || 'none')}
            onChange={(v) => e.setGroupBy(v)}
            options={groupOpts.map(g => ({ id: g.key, label: g.label }))}
          />
        </div>
        <span className="tabular-nums text-xs text-text-secondary dark:text-text-dark-secondary">
          <strong className="text-text-primary dark:text-text-dark-primary">{e.total.toLocaleString()}</strong> records · {totalLabel(activeTab, e)}
        </span>
      </div>

      {/* Body */}
      {e.loading && rows.length === 0 ? (
        <LoadingList surface="plain" rows={6} label="Loading transactions" />
      ) : e.error ? (
        <div className="px-5 py-10 text-center text-sm text-error-700 dark:text-error-300">{e.error}</div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-12 text-center text-sm text-accent-500 dark:text-text-dark-tertiary">No {activeTab} in range.</div>
      ) : grouped ? (
        renderGrouped()
      ) : (
        renderFlat()
      )}

      {/* Show all */}
      {e.hasMore && (
        <div className="px-5 py-3 border-b border-border-light dark:border-border-dark text-center">
          <button
            type="button"
            onClick={() => e.showAll()}
            className="text-xs font-semibold text-primary-700 dark:text-primary-dark-400 hover:underline"
          >
            Show all {e.total.toLocaleString()} rows
          </button>
        </div>
      )}

      {/* Caveat */}
      <div className="flex items-start gap-[7px] px-5 pt-[11px] pb-3.5 text-accent-500 dark:text-text-dark-tertiary">
        <Info className="w-[13px] h-[13px] flex-none mt-px" />
        <span className="text-[11px] leading-snug">{caveat}</span>
      </div>

      {modalRecord && (
        modalRecord.entity === 'contributions'
          ? <CFRecordModal contribution={modalRecord.record} open onClose={() => setModalRecord(null)} />
          : <CFTransactionModal entity={modalRecord.entity as 'expenditures' | 'loans'} record={modalRecord.record} open onClose={() => setModalRecord(null)} />
      )}
    </div>
  );
};

export default CFTransactionsExplorer;
